import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';

// Query Keys
export const vendorProfileKeys = {
  all: ['vendor-profile'],
  profile: () => [...vendorProfileKeys.all, 'profile'],
  business: () => [...vendorProfileKeys.all, 'business'],
};

// API Functions
const vendorProfileApi = {
  // Get vendor profile
  getProfile: async () => {
    const response = await api.get('/vendor/profile');
    return response.data;
  },

  // Update vendor profile (including image_url)
  updateProfile: async (data) => {
    const response = await api.put('/vendor/profile', data);
    return response.data;
  },

  // Update business profile
  updateBusinessProfile: async (data) => {
    const response = await api.put('/vendor/profile', data);
    return response.data;
  },

  // Change password
  changePassword: async (data) => {
    const response = await api.post('/user/profile/change-password', {
      current_password: data.currentPassword,
      new_password: data.newPassword,
      new_password_confirmation: data.confirmPassword
    });
    return response.data;
  },

  getTwoFactorStatus: async () => {
    const response = await api.get('/vendor/two-factor/status');
    return response.data;
  },

  setupTwoFactor: async () => {
    const response = await api.post('/vendor/two-factor/setup');
    return response.data;
  },

  confirmTwoFactor: async (code) => {
    const response = await api.post('/vendor/two-factor/confirm', { code });
    return response.data;
  },

  cancelTwoFactorSetup: async () => {
    const response = await api.post('/vendor/two-factor/cancel');
    return response.data;
  },

  // Enable two-factor authentication (alias → setup)
  enableTwoFactor: async () => {
    const response = await api.post('/vendor/two-factor/setup');
    return response.data;
  },

  // Disable two-factor authentication
  disableTwoFactor: async (payload = {}) => {
    const response = await api.post('/vendor/two-factor/disable', payload);
    return response.data;
  },

  regenerateTwoFactorRecoveryCodes: async (password) => {
    const response = await api.post('/vendor/two-factor/recovery-codes', { password });
    return response.data;
  },
};

// React Query Hooks

// Get vendor profile
export const useVendorProfile = (options = {}) => {
  return useQuery({
    queryKey: vendorProfileKeys.profile(),
    queryFn: () => vendorProfileApi.getProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
};

// Update vendor profile mutation
export const useUpdateVendorProfile = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => vendorProfileApi.updateProfile(data),
    onSuccess: (data) => {
      // Invalidate and refetch profile data
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.profile() });
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.business() });
      
      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      // Call custom onError if provided
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Update business profile mutation
export const useUpdateBusinessProfile = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => vendorProfileApi.updateBusinessProfile(data),
    onSuccess: (data) => {
      // Invalidate and refetch profile data
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.profile() });
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.business() });
      
      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      // Call custom onError if provided
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Change password mutation
export const useChangePassword = (options = {}) => {
  return useMutation({
    mutationFn: (data) => vendorProfileApi.changePassword({
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword
    }),
    onSuccess: (data) => {
      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      // Call custom onError if provided
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Enable two-factor authentication mutation
export const useEnableTwoFactor = (options = {}) => {
  return useMutation({
    mutationFn: () => vendorProfileApi.enableTwoFactor(),
    onSuccess: (data) => {
      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      // Call custom onError if provided
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Disable two-factor authentication mutation
export const useDisableTwoFactor = (options = {}) => {
  return useMutation({
    mutationFn: (payload) => vendorProfileApi.disableTwoFactor(payload || {}),
    onSuccess: (data) => {
      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      // Call custom onError if provided
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

export const getTwoFactorStatus = () => vendorProfileApi.getTwoFactorStatus();
export const setupTwoFactor = () => vendorProfileApi.setupTwoFactor();
export const confirmTwoFactor = (code) => vendorProfileApi.confirmTwoFactor(code);
export const cancelTwoFactorSetup = () => vendorProfileApi.cancelTwoFactorSetup();
export const disableTwoFactor = (payload) => vendorProfileApi.disableTwoFactor(payload || {});
export const regenerateTwoFactorRecoveryCodes = (password) =>
  vendorProfileApi.regenerateTwoFactorRecoveryCodes(password);

